import React, { useState, useEffect } from 'react';

interface ProfileData {
  fullName: string;
  phone: string;
  profileImage: File | null;
  profileImageUrl?: string;
  businessName: string;
  licenseNumber: string;
  yearsOfExperience: number;
  bio: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  website: string;
  specialties: string[];
  services: string[];
  treatmentCategories: string[];
  pricingRange: string;
  virtualConsultations: boolean;
  education: Array<{
    degree: string[];
    institution: string;
    year: string;
  }>;
  certifications: Array<{
    name: string[];
    issuer: string;
    year: string;
  }>;
  affiliations: string[];
  insuranceAccepted: string[];
  languagesSpoken: string[];
}

interface Step1BasicInfoProps {
  data: ProfileData;
  updateData: (updates: Partial<ProfileData>) => void;
}

const Step1BasicInfo: React.FC<Step1BasicInfoProps> = ({ data, updateData }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(data.profileImageUrl || null);

  useEffect(() => {
    if (data.profileImageUrl && !imagePreview) {
      setImagePreview(data.profileImageUrl);
    }
  }, [data.profileImageUrl, imagePreview]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      updateData({ profileImage: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="font-['Ovo',_serif] text-[22px] sm:text-[26px] lg:text-[28px] text-[#1f1f1f] mb-4 sm:mb-6">
        Let's start with your basic information
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label className="block mb-2 font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
            Full Name *
          </label>
          <input
            type="text"
            value={data.fullName}
            onChange={(e) => updateData({ fullName: e.target.value })}
            className="w-full px-4 py-3 sm:py-2.5 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
            placeholder="Enter your full name"
          />
        </div>

        <div>
          <label className="block mb-2 font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
            Phone Number *
          </label>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => updateData({ phone: e.target.value })}
            className="w-full px-4 py-3 sm:py-2.5 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <div>
        <label className="block mb-2 font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
          Profile Picture
        </label>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {(imagePreview || data.profileImageUrl) && (
            <img
              src={imagePreview || data.profileImageUrl}
              alt="Profile"
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-[#e5e5ea] flex-shrink-0"
            />
          )}
          <div className="w-full sm:w-auto">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="text-sm text-[#767472] w-full"
              id="profile-image-input"
            />
            {data.profileImage && (
              <p className="mt-1 text-xs text-green-600">
                ✓ New image selected: {data.profileImage.name}
              </p>
            )}
          </div>
        </div>
        <p className="mt-2 text-xs text-[#767472]">
          Upload a professional photo. Max file size: 5MB. Supported formats: JPG, PNG, GIF
        </p>
      </div>
    </div>
  );
};

export default Step1BasicInfo;
